import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getErrorMessage } from '@/lib/errorMessages';
import type { User, NicheType } from '@/types';
import { toast } from 'sonner';
import { storeCredentials, clearCredentials, autoLogin } from '@/lib/auth/insecureAuth';
import { storeUser, getStoredUser, initStorageSync } from '@/lib/auth/storage';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, data: { name: string; niche_type: NicheType }) => Promise<{ error: any, user: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try auto-login on mount
    autoLogin(supabase).then(async (success) => {
      if (success) {
        const storedUser = getStoredUser();
        if (storedUser) {
          setUser(storedUser);
        }
      }
      setLoading(false);
    });

    // Initialize storage sync
    initStorageSync(
      async (auth) => {
        if (auth) {
          await autoLogin(supabase);
        } else {
          setUser(null);
        }
      },
      (userData) => {
        setUser(userData);
      }
    );
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });

      if (error) {
        return { error: getErrorMessage(error) };
      }

      if (data.user) {
        const { data: userData } = await supabase
          .from('users')
          .select('*')
          .eq('id', data.user.id)
          .single();
          
        if (userData) {
          setUser(userData);
          storeUser(userData);
          storeCredentials(email, password);
        }
      }

      return { error: null };
    } catch (error) {
      return { error: getErrorMessage(error) };
    }
  };

  const signUp = async (email: string, password: string, userData: { name: string; niche_type: NicheType }) => {
    try {
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: {
            name: userData.name,
            role: 'corretor',
            niche_type: userData.niche_type
          }
        }
      });

      if (error) {
        return { error: getErrorMessage(error), user: null };
      }

      if (!data.user) {
        return { 
          error: 'Nenhum dado de usuário retornado', 
          user: null 
        };
      }

      const { error: profileError } = await supabase
        .from('users')
        .insert([{
          id: data.user.id,
          email: email,
          name: userData.name,
          role: 'corretor',
          niche_type: userData.niche_type,
          listing_limit: 5,
          is_blocked: false
        }]);

      if (profileError) {
        await supabase.auth.admin.deleteUser(data.user.id);
        return { error: getErrorMessage(profileError), user: null };
      }

      storeCredentials(email, password);
      return { error: null, user: data.user };
    } catch (error) {
      return { error: getErrorMessage(error), user: null };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return { error: getErrorMessage(error) };
      }

      return { error: null };
    } catch (error) {
      return { error: getErrorMessage(error) };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      clearCredentials();
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};